/**
 * WalkerDrawer — Admin-only "Walker" manuscript editing panel.
 *
 * Shows:
 *   • A locked button for non-admin users
 *   • Job progress (chapter N of M)
 *   • Per-chapter result cards with Accept / Reject actions
 *   • A diff view modal for comparing original vs edited prose
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlignLeft, BookOpenCheck, CheckCheck, ChevronDown, ChevronUp, CircleCheck, CircleX,
  Clock, Database, History, Loader2, Lock, RefreshCw, ShieldAlert, X, Zap, Cpu,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { editWalkApi } from '@/lib/api';

// ─── Status helpers ────────────────────────────────────────────────────────
const STATUS_LABEL = {
  pending:   'Queued',
  running:   'Running',
  done:      'Complete',
  cancelled: 'Cancelled',
  error:     'Error',
  stalled:   'Stalled',
};
const STATUS_COLOR = {
  pending:   'text-amber-600',
  running:   'text-blue-600',
  done:      'text-emerald-600',
  cancelled: 'text-neutral-400',
  error:     'text-rose-600',
  stalled:   'text-amber-500',
};
const CHAPTER_STATUS_COLOR = {
  running:  'bg-blue-100 text-blue-700',
  done:     'bg-emerald-100 text-emerald-700',
  accepted: 'bg-emerald-200 text-emerald-900 font-semibold',
  rejected: 'bg-neutral-100 text-neutral-500 line-through',
  error:    'bg-rose-100 text-rose-700',
};

// ─── Diff view modal ───────────────────────────────────────────────────────
function DiffModal({ result, onClose }) {
  if (!result) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-5 py-3 border-b shrink-0">
          <DialogTitle className="text-[14px]">
            Chapter {result.chapter_number} — "{result.chapter_title}"
          </DialogTitle>
          {result.analysis_brief && (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
              <p className="font-semibold uppercase tracking-wider text-[13px] text-amber-700 mb-1">
                Opus 4.6 — Structural Analysis Brief
              </p>
              <pre className="whitespace-pre-wrap font-sans leading-relaxed">{result.analysis_brief}</pre>
            </div>
          )}
        </DialogHeader>
        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className="flex-1 overflow-y-auto p-4 border-r">
            <p className="text-[13px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">Original</p>
            <pre className="whitespace-pre-wrap font-serif text-[13px] leading-relaxed text-foreground">
              {result.original_content}
            </pre>
          </div>
          <div className="flex-1 overflow-y-auto p-4 bg-emerald-50/40">
            <p className="text-[13px] font-semibold uppercase tracking-widest text-emerald-700 mb-3">Edited Draft</p>
            <pre className="whitespace-pre-wrap font-serif text-[13px] leading-relaxed text-foreground">
              {result.edited_content}
            </pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Chapter result card ────────────────────────────────────────────────────
function ChapterCard({ r, onAccept, onReject, onViewDiff }) {
  const [expanded,      setExpanded]      = useState(false);
  const [viewMode,      setViewMode]      = useState(null);   // null | 'original' | 'edited'
  const [detail,        setDetail]        = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const chipClass  = CHAPTER_STATUS_COLOR[r.status] || 'bg-neutral-100 text-neutral-600';
  const canAct     = r.status === 'done';
  const canToggle  = r.status === 'done' || r.status === 'accepted' || r.status === 'rejected';

  const ensureDetail = async () => {
    if (detail || loadingDetail) return;
    setLoadingDetail(true);
    try { const { result } = await editWalkApi.resultDetail(r.id); setDetail(result); }
    catch { /* silent */ }
    finally { setLoadingDetail(false); }
  };

  const handleToggle = (mode) => {
    if (viewMode === mode) { setViewMode(null); return; }
    setViewMode(mode);
    ensureDetail();
  };

  const displayText = viewMode === 'original'
    ? (detail?.original_content ?? (loadingDetail ? null : ''))
    : viewMode === 'edited'
    ? (detail?.edited_content   ?? (loadingDetail ? null : ''))
    : null;

  return (
    <div
      className="rounded-lg border border-border bg-card overflow-hidden"
      data-testid={`walker-chapter-${r.chapter_number}`}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-muted/40"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[13.5px] font-semibold text-muted-foreground w-8 shrink-0">
            {String(r.chapter_number).padStart(2, '0')}
          </span>
          <span className="text-[14px] font-medium truncate">{r.chapter_title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className={`text-[13px] rounded-full px-2 py-0.5 ${chipClass}`}>
            {r.status === 'running' ? <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> : null}
            {STATUS_LABEL[r.status] || r.status}
          </span>
          {/* Cache indicator — green = cache hit, red = no cache, dim = not yet run */}
          {(() => {
            const done = r.status === 'done' || r.status === 'accepted' || r.status === 'rejected';
            const hit  = (r.prose_cache_read || 0) > 0;
            if (!done) return null;
            return (
              <Database
                className={`h-3 w-3 shrink-0 ${hit ? 'text-emerald-500' : 'text-rose-500'}`}
                title={hit
                  ? `Cache hit — ${Math.round((r.prose_cache_read || 0) / 1000)}k tokens read from cache`
                  : 'No cache hit — manuscript encoded fresh this chapter'}
                data-testid={`walker-cache-dot-${r.chapter_number}`}
              />
            );
          })()}
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-3 py-3 space-y-3">
          {r.error && (
            <p className="text-[13.5px] text-rose-600 bg-rose-50 rounded p-2">{r.error}</p>
          )}

          {/* ── Opus 4.6 Analysis Brief — inline, no extra API call needed ── */}
          {r.analysis_brief && (
            <div className="rounded-md border border-amber-200/70 bg-amber-50/60 overflow-hidden"
              data-testid={`walker-analysis-brief-${r.chapter_number}`}>
              <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-amber-200/50">
                <span className="text-[13px] font-semibold uppercase tracking-wider text-amber-700">
                  Opus 4.6 — Structural Analysis
                </span>
              </div>
              <pre className="px-2.5 py-2 text-[13px] text-amber-900 whitespace-pre-wrap font-sans leading-relaxed">
                {r.analysis_brief}
              </pre>
            </div>
          )}
          {canAct && (
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-[13.5px]"
                onClick={() => onViewDiff(r)}
                data-testid={`walker-view-diff-${r.chapter_number}`}
              >
                Full Compare
              </Button>
              {r.status === 'done' && (
                <>
                  <Button
                    size="sm"
                    className="h-7 gap-1.5 text-[13.5px] bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => onAccept(r.id)}
                    data-testid={`walker-accept-${r.chapter_number}`}
                  >
                    <CircleCheck className="h-3 w-3" /> Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 text-[13.5px] text-rose-600 border-rose-300 hover:bg-rose-50"
                    onClick={() => onReject(r.id)}
                    data-testid={`walker-reject-${r.chapter_number}`}
                  >
                    <CircleX className="h-3 w-3" /> Reject
                  </Button>
                </>
              )}
              {r.status === 'accepted' && (
                <span className="text-[13px] text-emerald-700 flex items-center gap-1">
                  <CircleCheck className="h-3 w-3" /> Edit accepted
                </span>
              )}
              {r.status === 'rejected' && (
                <span className="text-[13px] text-neutral-500 flex items-center gap-1">
                  <CircleX className="h-3 w-3" /> Rejected — original kept
                </span>
              )}
            </div>
          )}

          {/* ── Inline original / edited toggle (Feature 2) ─────────────── */}
          {canToggle && (
            <div className="space-y-2">
              <div className="flex gap-1 rounded-md border border-border p-0.5 w-fit">
                <button
                  type="button"
                  onClick={() => handleToggle('original')}
                  className={`rounded px-2.5 py-1 text-[13px] font-medium transition-colors ${
                    viewMode === 'original'
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`walker-toggle-original-${r.chapter_number}`}
                >
                  <AlignLeft className="inline h-3 w-3 mr-1" />Original
                </button>
                <button
                  type="button"
                  onClick={() => handleToggle('edited')}
                  className={`rounded px-2.5 py-1 text-[13px] font-medium transition-colors ${
                    viewMode === 'edited'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`walker-toggle-edited-${r.chapter_number}`}
                >
                  <Zap className="inline h-3 w-3 mr-1" />Edited Draft
                </button>
              </div>
              {viewMode && (
                <div className="rounded-md border border-border bg-muted/30 max-h-56 overflow-y-auto p-3">
                  {loadingDetail ? (
                    <div className="flex items-center gap-2 text-[13.5px] text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                    </div>
                  ) : displayText ? (
                    <pre className="whitespace-pre-wrap font-serif text-[13.5px] leading-relaxed text-foreground">
                      {displayText}
                    </pre>
                  ) : (
                    <p className="text-[13.5px] text-muted-foreground italic">No content available.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main drawer ────────────────────────────────────────────────────────────
export default function WalkerDrawer({ open, onClose, projectId, isAdmin }) {
  const [tab, setTab]             = useState('current');   // 'current' | 'history'
  const [job, setJob]             = useState(null);
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [starting, setStarting]   = useState(false);
  const [diffResult, setDiffResult] = useState(null);
  // Chapter range
  const [fromChapter, setFromChapter] = useState('');
  const [toChapter,   setToChapter]   = useState('');
  // Bulk accept
  const [acceptingAll, setAcceptingAll] = useState(false);
  // History tab
  const [historyJobs,     setHistoryJobs]     = useState([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [viewingHistoric, setViewingHistoric] = useState(null);   // job being viewed from history
  // Walker model info (loaded once on open)
  const [walkerModels, setWalkerModels] = useState(null);   // { defaults: {analysis, prose}, models: [] }
  // Per-run model selection (override defaults)
  const [analysisModel, setAnalysisModel] = useState('');   // '' = use server default
  const [proseModel,    setProseModel]    = useState('');   // '' = use project default
  const [autoAccept,    setAutoAccept]    = useState(false); // silent-batch: skip review
  const [deepPlotAudit, setDeepPlotAudit] = useState(false); // hook scores + arc drift
  const [depth,         setDepth]         = useState('full_structural'); // 'quick_pass' | 'full_structural'
  const pollRef = useRef(null);

  const loadLatest = useCallback(async () => {
    if (!projectId || !isAdmin) return;
    try {
      const { job: j } = await editWalkApi.latest(projectId);
      setJob(j);
      if (j?.id) {
        const { results: r } = await editWalkApi.results(j.id);
        setResults(r || []);
      }
    } catch { /* silent */ }
  }, [projectId, isAdmin]);

  const loadHistory = useCallback(async () => {
    if (!projectId || !isAdmin) return;
    setHistoryLoading(true);
    try {
      const { jobs } = await editWalkApi.history(projectId, { limit: 20 });
      setHistoryJobs(jobs || []);
    } catch { toast.error('Could not load walker history.'); }
    finally { setHistoryLoading(false); }
  }, [projectId, isAdmin]);

  const viewHistoricJob = async (j) => {
    setLoading(true);
    try {
      const { results: r } = await editWalkApi.results(j.id);
      setViewingHistoric(j);
      setResults(r || []);
      setJob(j);
      setTab('current');
    } catch { toast.error('Could not load job results.'); }
    finally { setLoading(false); }
  };

  // Poll while running
  useEffect(() => {
    if (!open || !isAdmin) return;
    loadLatest();
    // Load Walker model info once on open; initialise selectors to server defaults
    editWalkApi.models().then(d => {
      setWalkerModels(d);
      setAnalysisModel(d?.defaults?.analysis || 'claude-opus-4-6');
      setProseModel(d?.defaults?.prose    || 'claude-opus-4-5');
    }).catch(() => {});
    pollRef.current = setInterval(() => {
      if (job?.status === 'running' || job?.status === 'pending') loadLatest();
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [open, isAdmin, loadLatest, job?.status]);

  // Load history when tab switches
  useEffect(() => {
    if (tab === 'history' && isAdmin) loadHistory();
  }, [tab, isAdmin, loadHistory]);

  const handleStart = async () => {
    setStarting(true);
    setViewingHistoric(null);
    try {
      const body = {};
      if (fromChapter) body.from_chapter = parseInt(fromChapter, 10);
      if (toChapter)   body.to_chapter   = parseInt(toChapter,   10);
      if (analysisModel) body.analysis_model_id = analysisModel;
      if (proseModel)    body.prose_model_id    = proseModel;
      if (autoAccept)    body.auto_accept        = true;
      if (deepPlotAudit) body.deep_plot_audit   = true;
      body.depth = depth;
      await editWalkApi.start(projectId, body);
      await loadLatest();
      const rangeNote = (body.from_chapter || body.to_chapter)
        ? ` (Ch ${body.from_chapter ?? 1}–${body.to_chapter ?? 'end'})`
        : '';
      toast.success(`Walker started${rangeNote} — working through chapters now.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Walker.');
    } finally {
      setStarting(false);
    }
  };

  // Feature 1: Accept All
  const handleAcceptAll = async () => {
    if (!job?.id) return;
    const doneResults = results.filter(r => r.status === 'done');
    if (!doneResults.length) return;
    setAcceptingAll(true);
    try {
      const res = await editWalkApi.applyAll(job.id);
      const n = res?.applied ?? doneResults.length;
      toast.success(`${n} chapter edit${n === 1 ? '' : 's'} applied to manuscript.`);
      await loadLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply all failed.');
    } finally {
      setAcceptingAll(false);
    }
  };

  const handleCancel = async () => {
    if (!job?.id) return;
    await editWalkApi.cancel(job.id);
    await loadLatest();
    toast.info('Walker cancelled.');
  };

  const handleResume = async () => {
    if (!job?.id) return;
    try {
      const res = await editWalkApi.resume(job.id);
      toast.success(`Walker resumed from chapter ${res.resume_from_chapter}.`);
      await loadLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed.');
    }
  };

  const handleAccept = async (resultId) => {
    try {
      await editWalkApi.accept(resultId);
      toast.success('Edit accepted — chapter updated.');
      await loadLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Accept failed.');
    }
  };

  const handleReject = async (resultId) => {
    try {
      await editWalkApi.reject(resultId);
      toast.info('Edit rejected — original kept.');
      await loadLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed.');
    }
  };

  const handleViewDiff = async (r) => {
    setLoading(true);
    try {
      const { result } = await editWalkApi.resultDetail(r.id);
      setDiffResult(result);
    } catch { toast.error('Could not load chapter content.'); }
    finally { setLoading(false); }
  };

  if (!open) return null;

  const pct = job?.chapters_total > 0
    ? Math.round((job.chapters_done / job.chapters_total) * 100)
    : 0;
  const isRunning = job?.status === 'running' || job?.status === 'pending';
  const accepted  = results.filter(r => r.status === 'accepted').length;
  const rejected  = results.filter(r => r.status === 'rejected').length;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-[480px] max-w-full flex-col bg-background shadow-2xl"
        data-testid="walker-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            <span className="text-[14px] font-semibold">Walker</span>
            <span className="text-[13px] rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-medium uppercase tracking-wider">Admin</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
            aria-label="Close Walker"
            data-testid="walker-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Admin guard */}
        {!isAdmin && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <Lock className="h-10 w-10 text-muted-foreground" />
            <p className="text-[13px] font-semibold">Admin access required</p>
            <p className="text-[13.5px] text-muted-foreground">
              The Walker is restricted to admin staff only.
            </p>
          </div>
        )}

        {isAdmin && (
          <>
            {/* Tab bar */}
            <div className="flex border-b shrink-0">
              {[
                { key: 'current', label: 'Current Run', icon: Zap },
                { key: 'history', label: 'History', icon: History },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-testid={`walker-tab-${key}`}
                  className={
                    'flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[13.5px] font-medium transition-colors '
                    + (tab === key
                      ? 'border-b-2 border-amber-500 text-amber-600'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* ── CURRENT RUN TAB ── */}
            {tab === 'current' && (
              <>
                {/* Controls */}
                <div className="border-b px-4 py-3 shrink-0 space-y-3">
                  {viewingHistoric && (
                    <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-[13.5px] text-amber-700">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      Viewing past run from {new Date(viewingHistoric.created_at).toLocaleDateString()}.
                      <button type="button" onClick={() => { setViewingHistoric(null); loadLatest(); }}
                        className="ml-auto underline hover:no-underline">Latest</button>
                    </div>
                  )}
                  <p className="text-[13.5px] text-muted-foreground leading-relaxed">
                    Walks every chapter sequentially with the novel framework, character anchors,
                    and rolling story state. Rewrites prose without changing plot events.
                    Each edit saves as a draft — accept or reject per chapter.
                  </p>

                  {/* Walker Model Pair — read-only info + per-run dropdowns */}
                  {walkerModels && (() => {
                    const models = walkerModels.models || [];
                    const find = (id) => models.find(m => m.id === id) || { label: id, recommended_for: '' };
                    return (
                      <div className="rounded-md border border-border bg-muted/20 px-3 py-2.5 text-[13px] space-y-2" data-testid="walker-model-info-card">
                        <div className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                          <Cpu className="h-3 w-3" /> Two-Pass Model Pair
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {/* Analysis model */}
                          <div className="space-y-1">
                            <label className="text-[13px] uppercase tracking-wider text-muted-foreground">Pass 1 · Analysis</label>
                            <select
                              value={analysisModel}
                              onChange={e => setAnalysisModel(e.target.value)}
                              disabled={job?.status === 'running' || job?.status === 'pending'}
                              data-testid="walker-analysis-model-select"
                              className="w-full rounded-sm border border-border bg-background px-2 py-1 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                            >
                              {models.map(m => (
                                <option key={m.id} value={m.id}>{m.label}</option>
                              ))}
                            </select>
                            {(() => { const m = find(analysisModel); return m.recommended_for ? <p className="text-[13px] text-muted-foreground leading-tight">{m.recommended_for}</p> : null; })()}
                          </div>
                          {/* Prose model */}
                          <div className="space-y-1">
                            <label className="text-[13px] uppercase tracking-wider text-muted-foreground">Pass 2 · Prose</label>
                            <select
                              value={proseModel}
                              onChange={e => setProseModel(e.target.value)}
                              disabled={job?.status === 'running' || job?.status === 'pending'}
                              data-testid="walker-prose-model-select"
                              className="w-full rounded-sm border border-border bg-background px-2 py-1 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                            >
                              {models.map(m => (
                                <option key={m.id} value={m.id}>{m.label}</option>
                              ))}
                            </select>
                            {(() => { const m = find(proseModel); return m.recommended_for ? <p className="text-[13px] text-muted-foreground leading-tight">{m.recommended_for}</p> : null; })()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Chapter range selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-[13px] text-muted-foreground shrink-0">Chapter range:</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="From"
                      value={fromChapter}
                      onChange={e => setFromChapter(e.target.value)}
                      className="h-7 w-16 rounded-md border border-border bg-background px-2 text-[13.5px] text-center focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="walker-from-chapter"
                    />
                    <span className="text-[13px] text-muted-foreground">to</span>
                    <input
                      type="number"
                      min="1"
                      placeholder="End"
                      value={toChapter}
                      onChange={e => setToChapter(e.target.value)}
                      className="h-7 w-16 rounded-md border border-border bg-background px-2 text-[13.5px] text-center focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="walker-to-chapter"
                    />
                    {(fromChapter || toChapter) && (
                      <button
                        type="button"
                        onClick={() => { setFromChapter(''); setToChapter(''); }}
                        className="text-[13px] text-muted-foreground hover:text-foreground underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Silent Batch toggle */}
                  <label
                    className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-muted/20 px-3 py-2.5"
                    data-testid="walker-auto-accept-label"
                  >
                    <input
                      type="checkbox"
                      checked={autoAccept}
                      onChange={e => setAutoAccept(e.target.checked)}
                      disabled={starting || isRunning}
                      data-testid="walker-auto-accept-toggle"
                      className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-amber-500"
                    />
                    <div className="space-y-0.5">
                      <p className="text-[13.5px] font-semibold leading-tight text-foreground">
                        Silent Batch — apply edits automatically
                      </p>
                      <p className="text-[13px] leading-snug text-muted-foreground">
                        Each chapter is written back to the manuscript as it finishes. No review step. Use on stable, trusted manuscripts only.
                      </p>
                    </div>
                  </label>

                  {/* Deep Plot Audit toggle */}
                  <label
                    className="flex cursor-pointer items-start gap-2.5 rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5"
                    data-testid="walker-deep-plot-audit-label"
                  >
                    <input
                      type="checkbox"
                      checked={deepPlotAudit}
                      onChange={e => setDeepPlotAudit(e.target.checked)}
                      disabled={starting || isRunning}
                      data-testid="walker-deep-plot-audit-toggle"
                      className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-primary"
                    />
                    <div className="space-y-0.5">
                      <p className="text-[13.5px] font-semibold leading-tight text-foreground">
                        Deep Plot Audit
                      </p>
                      <p className="text-[13px] leading-snug text-muted-foreground">
                        Injects your Plot Blueprint into every chapter pass — scores hook strength, flags arc drift, plot holes and pacing. Requires an extracted plot. Aimed at the ≥ 85% sales readiness threshold.
                      </p>
                    </div>
                  </label>

                  {/* Depth Mode selector */}
                  <div className="rounded-md border border-border bg-card p-3" data-testid="walker-depth-selector">
                    <p className="mb-2 text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Edit Depth
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDepth('quick_pass')}
                        disabled={starting || isRunning}
                        data-testid="walker-depth-quick"
                        className={`flex-1 rounded-sm border px-3 py-2 text-left text-[13.5px] transition-colors disabled:opacity-60 ${
                          depth === 'quick_pass'
                            ? 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200'
                            : 'border-border bg-card hover:bg-secondary'
                        }`}
                      >
                        <div className="font-semibold">Quick Pass</div>
                        <div className="text-[13px] text-muted-foreground mt-0.5">Line-edit only — Haiku, no analysis pass. Faster &amp; cheaper.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDepth('full_structural')}
                        disabled={starting || isRunning}
                        data-testid="walker-depth-full"
                        className={`flex-1 rounded-sm border px-3 py-2 text-left text-[13.5px] transition-colors disabled:opacity-60 ${
                          depth === 'full_structural'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-card hover:bg-secondary'
                        }`}
                      >
                        <div className="font-semibold">Full Structural</div>
                        <div className="text-[13px] text-muted-foreground mt-0.5">Opus analysis + prose rewrite. Deep structural &amp; line edits.</div>
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      className="gap-1.5 text-[13.5px] bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={handleStart}
                      disabled={starting || isRunning}
                      data-testid="walker-start-btn"
                    >
                      {starting
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Zap className="h-3.5 w-3.5" />}
                      {job ? 'Re-run Walker' : 'Run Walker'}
                    </Button>
                    {isRunning && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-[13.5px]"
                        onClick={handleCancel}
                        data-testid="walker-cancel-btn"
                      >
                        Cancel
                      </Button>
                    )}
                    {job?.status === 'stalled' && (
                      <Button
                        size="sm"
                        className="gap-1.5 text-[13.5px] bg-amber-500 hover:bg-amber-600 text-white"
                        onClick={handleResume}
                        data-testid="walker-resume-btn"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Resume from ch. {(job.chapters_done || 0) + 1}
                      </Button>
                    )}
                    {/* Accept All / Finish & Apply All */}
                    {results.some(r => r.status === 'done') && (
                      <Button
                        size="sm"
                        className={cn(
                          'gap-1.5 text-[13.5px] text-white',
                          job?.status === 'done'
                            ? 'bg-emerald-600 hover:bg-emerald-700'
                            : 'bg-emerald-600/80 hover:bg-emerald-600',
                        )}
                        onClick={handleAcceptAll}
                        disabled={acceptingAll}
                        data-testid="walker-accept-all-btn"
                      >
                        {acceptingAll
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <CheckCheck className="h-3.5 w-3.5" />}
                        {job?.status === 'done'
                          ? `Finish & Apply All (${results.filter(r => r.status === 'done').length})`
                          : `Apply edits so far (${results.filter(r => r.status === 'done').length})`}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-8 w-8 p-0"
                      onClick={loadLatest}
                      title="Refresh"
                      data-testid="walker-refresh-btn"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Progress */}
                  {job && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[13.5px]">
                        <span className={STATUS_COLOR[job.status] || 'text-muted-foreground'}>
                          {STATUS_LABEL[job.status] || job.status}
                          {isRunning && (
                            <> — chapter {job.chapters_done + 1} of {job.chapters_total}</>
                          )}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {job.auto_accept && (
                            <span
                              className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-wider text-amber-600"
                              data-testid="walker-silent-batch-badge"
                            >
                              Silent Batch
                            </span>
                          )}
                          <span className="text-muted-foreground">
                            {accepted} accepted · {rejected} rejected
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-amber-400 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-[13px] text-muted-foreground">
                        {pct}% complete · {job.chapters_done} of {job.chapters_total} chapters processed
                      </p>
                      {job.error && (
                        <p className={`text-[13px] flex items-center gap-1 ${job.status === 'stalled' ? 'text-amber-600' : 'text-rose-600'}`}>
                          <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> {job.error}
                        </p>
                      )}

                      {/* ── Cache Hit Dashboard — visible when walk is complete ── */}
                      {job.status === 'done' && job.token_usage && (
                        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[13px] space-y-1"
                          data-testid="walker-cache-dashboard">
                          <p className="font-semibold text-foreground text-[13px] uppercase tracking-wider mb-1">
                            Token Usage &amp; Cache Stats
                          </p>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Cache hit rate</span>
                            <span className="font-mono font-semibold text-emerald-700">
                              {job.token_usage.cache_hit_rate_pct ?? 0}%
                            </span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Input (billed)</span>
                            <span className="font-mono">{((job.token_usage.total_input || 0) / 1000).toFixed(1)}k</span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Cache reads (×0.1 cost)</span>
                            <span className="font-mono text-emerald-600">{((job.token_usage.total_cache_read || 0) / 1000).toFixed(1)}k</span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Cache writes (×2 cost)</span>
                            <span className="font-mono">{((job.token_usage.total_cache_write || 0) / 1000).toFixed(1)}k</span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Output</span>
                            <span className="font-mono">{((job.token_usage.total_output || 0) / 1000).toFixed(1)}k</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Chapter results list */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="walker-results-list">
                  {results.length === 0 && !job && (
                    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                      <BookOpenCheck className="h-10 w-10 text-muted-foreground/40" />
                      <p className="text-[14px] text-muted-foreground">No edit walk has been run yet.</p>
                      <p className="text-[13.5px] text-muted-foreground">Click "Run Walker" to begin.</p>
                    </div>
                  )}
                  {results.map(r => (
                    <ChapterCard
                      key={r.id}
                      r={r}
                      onAccept={handleAccept}
                      onReject={handleReject}
                      onViewDiff={handleViewDiff}
                    />
                  ))}
                </div>
              </>
            )}

            {/* ── HISTORY TAB ── */}
            {tab === 'history' && (
              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="walker-history-list">
                {historyLoading && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!historyLoading && historyJobs.length === 0 && (
                  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                    <History className="h-10 w-10 text-muted-foreground/40" />
                    <p className="text-[14px] text-muted-foreground">No past runs found for this project.</p>
                  </div>
                )}
                {!historyLoading && historyJobs.map((j) => {
                  const acceptedCount = j.chapters_accepted ?? '—';
                  const rejectedCount = j.chapters_rejected ?? '—';
                  const dateStr = j.created_at
                    ? new Date(j.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '—';
                  const range = (j.from_chapter || j.to_chapter)
                    ? `Ch ${j.from_chapter ?? 1}–${j.to_chapter ?? 'end'}`
                    : `All ${j.chapters_total ?? '?'} chapters`;
                  return (
                    <div
                      key={j.id}
                      className="rounded-lg border border-border bg-card px-3 py-2.5 space-y-1.5"
                      data-testid={`walker-history-job-${j.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-[13.5px] font-medium truncate">{dateStr}</span>
                        </div>
                        <span className={`text-[13px] rounded-full px-2 py-0.5 ${
                          j.status === 'done'      ? 'bg-emerald-100 text-emerald-700'
                            : j.status === 'error'   ? 'bg-rose-100 text-rose-700'
                            : j.status === 'running' ? 'bg-blue-100 text-blue-700'
                            : j.status === 'stalled' ? 'bg-amber-100 text-amber-700'
                            : 'bg-neutral-100 text-neutral-500'
                        }`}>
                          {STATUS_LABEL[j.status] || j.status}
                        </span>
                      </div>
                      <div className="text-[13px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>{range}</span>
                        <span>{j.chapters_done ?? '?'} processed</span>
                        {acceptedCount !== '—' && <span className="text-emerald-700">{acceptedCount} accepted</span>}
                        {rejectedCount !== '—' && <span className="text-neutral-500">{rejectedCount} rejected</span>}
                        <span
                          className={j.auto_accept
                            ? 'font-medium text-amber-600'
                            : 'text-muted-foreground'}
                          data-testid={`walker-history-mode-${j.id}`}
                        >
                          {j.auto_accept ? 'Silent Batch' : 'Review mode'}
                        </span>
                      </div>
                      {j.status === 'done' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[13px] gap-1"
                          onClick={() => viewHistoricJob(j)}
                          data-testid={`walker-history-view-${j.id}`}
                        >
                          <BookOpenCheck className="h-3 w-3" />
                          View results
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </aside>

      {/* Diff modal */}
      {diffResult && (
        <DiffModal result={diffResult} onClose={() => setDiffResult(null)} />
      )}
    </>
  );
}
